import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import {
  Badge,
  Button,
  FormGrid,
  FormRow,
  Input,
  Label,
  Muted,
  Page,
  PageActions,
  PageHeader,
  PageHeaderMain,
  Select,
  Subtitle,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  SurfaceTitle,
  TabBar,
  TabBtn,
  Table,
  TableWrap,
  TextArea,
  Title,
  Toast,
} from '../components/ui';

interface Bid {
  _id: string;
  displayName: string;
  twitchUsername: string;
  twitchUserId: string;
  amount: number;
  isManual: boolean;
  createdAt: string;
}

interface Auction {
  _id: string;
  title: string;
  description: string;
  imageUrl: string;
  status: string;
  startingPrice: number;
  minimumBid: number;
  currentHighBid: number;
  minFollowMonths: number;
  announceBidsInChat?: boolean;
  endsAt?: string | null;
  closedAt?: string | null;
}

const statusLabel: Record<string, string> = {
  draft: 'Oculta',
  active: 'Activa',
  paused: 'Pausada',
  closed: 'Cerrada',
};

function toDatetimeLocal(value?: string | null) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const AuctionDetailAdminPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'datos' | 'pujas' | 'acciones'>('datos');
  const [auction, setAuction] = useState<Auction | null>(null);
  const [bids, setBids] = useState<Bid[]>([]);
  const [edit, setEdit] = useState({
    title: '',
    description: '',
    startingPrice: '',
    minimumBid: '',
    minFollowMonths: '',
    status: 'active',
    imageUrl: '',
    announceBidsInChat: false,
    endsAt: '',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [manual, setManual] = useState({ twitchUsername: '', displayName: '', amount: '' });
  const [block, setBlock] = useState({ twitchUserId: '', twitchUsername: '', reason: '' });
  const [message, setMessage] = useState('');

  const load = async () => {
    if (!id) return;
    const [auctionsRes, bidsRes] = await Promise.all([
      api.get('/auctions/admin/all'),
      api.get(`/auctions/admin/${id}/bids`),
    ]);
    const found = auctionsRes.data.find((a: Auction) => a._id === id);
    setAuction(found || null);
    setBids(bidsRes.data);
    if (found) {
      setEdit({
        title: found.title,
        description: found.description || '',
        startingPrice: String(found.startingPrice),
        minimumBid: String(found.minimumBid || 1),
        minFollowMonths: String(found.minFollowMonths ?? 3),
        status: found.status,
        imageUrl: found.imageUrl || '',
        announceBidsInChat: !!found.announceBidsInChat,
        endsAt: toDatetimeLocal(found.endsAt),
      });
    }
  };

  useEffect(() => { load(); }, [id]);

  const saveAuction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    const fd = new FormData();
    fd.append('title', edit.title);
    fd.append('description', edit.description);
    fd.append('startingPrice', edit.startingPrice);
    fd.append('minimumBid', edit.minimumBid);
    fd.append('minFollowMonths', edit.minFollowMonths);
    fd.append('status', edit.status);
    fd.append('announceBidsInChat', edit.announceBidsInChat ? 'true' : 'false');
    if (edit.endsAt) fd.append('endsAt', new Date(edit.endsAt).toISOString());
    else fd.append('clearEndsAt', 'true');
    if (edit.imageUrl) fd.append('imageUrl', edit.imageUrl);
    if (imageFile) fd.append('image', imageFile);

    await api.put(`/auctions/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setImageFile(null);
    setMessage('Subasta actualizada');
    load();
  };

  const saveScheduledClose = async () => {
    if (!id) return;
    const fd = new FormData();
    if (edit.endsAt) fd.append('endsAt', new Date(edit.endsAt).toISOString());
    else fd.append('clearEndsAt', 'true');
    await api.put(`/auctions/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    setMessage(edit.endsAt
      ? `Cierre programado: ${new Date(edit.endsAt).toLocaleString('es-AR')}`
      : 'Cierre programado quitado');
    load();
  };

  const finalizeNow = async () => {
    if (!id || !window.confirm('¿Finalizar la subasta ahora? Se anunciará ganador en chat y alerta OBS.')) return;
    await api.post(`/auctions/${id}/finalize`);
    setMessage('Subasta finalizada');
    load();
  };

  const deleteAuction = async () => {
    if (!id) return;
    if (!window.confirm(`¿Eliminar "${auction?.title}" permanentemente? Se borran también todas las pujas. Esta acción no se puede deshacer.`)) {
      return;
    }
    await api.delete(`/auctions/${id}`);
    navigate('/auctions');
  };

  const removeBid = async (bidId: string) => {
    if (!id || !window.confirm('¿Eliminar esta puja?')) return;
    await api.delete(`/auctions/${id}/bids/${bidId}`);
    load();
  };

  const addManualBid = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    await api.post(`/auctions/${id}/bids/manual`, {
      twitchUsername: manual.twitchUsername,
      displayName: manual.displayName || manual.twitchUsername,
      amount: parseInt(manual.amount, 10),
    });
    setManual({ twitchUsername: '', displayName: '', amount: '' });
    setMessage('Puja manual registrada');
    load();
  };

  const blockUser = async (e: React.FormEvent) => {
    e.preventDefault();
    await api.post('/auctions/admin/blocks', block);
    setBlock({ twitchUserId: '', twitchUsername: '', reason: '' });
    setMessage('Usuario bloqueado');
  };

  if (!auction) {
    return (
      <Page>
        <Title>Subasta no encontrada</Title>
        <Link to="/auctions">← Volver a subastas</Link>
      </Page>
    );
  }

  const isClosed = auction.status === 'closed';

  return (
    <Page>
      <PageHeader>
        <PageHeaderMain>
          <Title>{auction.title}</Title>
          <Subtitle>
            <Link to="/auctions">← Volver a subastas</Link>
            {' · '}
            <Badge $active={auction.status === 'active'}>{statusLabel[auction.status] || auction.status}</Badge>
            {' · '}Base ${auction.startingPrice} · Puja mín. ${auction.minimumBid || 1} · Actual ${auction.currentHighBid || 0}
          </Subtitle>
        </PageHeaderMain>
        <PageActions>
          <TabBar>
            <TabBtn type="button" $active={tab === 'datos'} onClick={() => setTab('datos')}>Datos</TabBtn>
            <TabBtn type="button" $active={tab === 'pujas'} onClick={() => setTab('pujas')}>Pujas</TabBtn>
            <TabBtn type="button" $active={tab === 'acciones'} onClick={() => setTab('acciones')}>Acciones</TabBtn>
          </TabBar>
        </PageActions>
      </PageHeader>

      {message && <Toast>{message}</Toast>}

      {tab === 'datos' && (
        <Surface>
          <SurfaceHeader>
            <SurfaceTitle>Editar subasta</SurfaceTitle>
          </SurfaceHeader>
          <SurfaceBody>
            <form onSubmit={saveAuction}>
              <FormGrid>
                <FormRow>
                  <Label>Título</Label>
                  <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} required />
                </FormRow>
                <FormRow>
                  <Label>Estado</Label>
                  <Select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} disabled={isClosed}>
                    <option value="draft">Oculta</option>
                    <option value="active">Activa</option>
                    <option value="paused">Pausada</option>
                    <option value="closed">Cerrada</option>
                  </Select>
                </FormRow>
                <FormRow style={{ gridColumn: '1 / -1' }}>
                  <Label>Descripción</Label>
                  <TextArea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} rows={5} />
                </FormRow>
                <FormRow>
                  <Label>Precio base (ARS)</Label>
                  <Input type="number" value={edit.startingPrice} onChange={(e) => setEdit({ ...edit, startingPrice: e.target.value })} />
                </FormRow>
                <FormRow>
                  <Label>Puja mínima (ARS)</Label>
                  <Input type="number" min={1} value={edit.minimumBid} onChange={(e) => setEdit({ ...edit, minimumBid: e.target.value })} />
                </FormRow>
                <FormRow>
                  <Label>Mín. meses de follow</Label>
                  <Input type="number" value={edit.minFollowMonths} onChange={(e) => setEdit({ ...edit, minFollowMonths: e.target.value })} />
                </FormRow>
                <FormRow>
                  <Label>Imagen (URL actual)</Label>
                  <Input value={edit.imageUrl} onChange={(e) => setEdit({ ...edit, imageUrl: e.target.value })} />
                </FormRow>
                <FormRow>
                  <Label>Reemplazar imagen</Label>
                  <Input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
                </FormRow>
                <FormRow style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: '#b8a9c9', fontSize: '0.84rem' }}>
                    <input
                      type="checkbox"
                      checked={edit.announceBidsInChat}
                      onChange={(e) => setEdit({ ...edit, announceBidsInChat: e.target.checked })}
                    />
                    Anunciar pujas en el chat de Twitch
                  </label>
                </FormRow>
              </FormGrid>
              <Button type="submit" style={{ marginTop: '0.75rem' }}>Guardar cambios</Button>
            </form>
          </SurfaceBody>
        </Surface>
      )}

      {tab === 'pujas' && (
        <>
          {!isClosed && (
            <Surface>
              <SurfaceHeader>
                <SurfaceTitle>Puja manual (excepción admin)</SurfaceTitle>
              </SurfaceHeader>
              <SurfaceBody>
                <form onSubmit={addManualBid}>
                  <FormGrid>
                    <FormRow>
                      <Label>Usuario Twitch</Label>
                      <Input value={manual.twitchUsername} onChange={(e) => setManual({ ...manual, twitchUsername: e.target.value })} required />
                    </FormRow>
                    <FormRow>
                      <Label>Display name</Label>
                      <Input value={manual.displayName} onChange={(e) => setManual({ ...manual, displayName: e.target.value })} />
                    </FormRow>
                    <FormRow>
                      <Label>Monto ARS</Label>
                      <Input type="number" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })} required />
                    </FormRow>
                  </FormGrid>
                  <Button type="submit" style={{ marginTop: '0.65rem' }}>Registrar puja manual</Button>
                </form>
              </SurfaceBody>
            </Surface>
          )}

          <Surface>
            <SurfaceHeader>
              <SurfaceTitle>Historial de pujas ({bids.length})</SurfaceTitle>
            </SurfaceHeader>
            <SurfaceBody $flush>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Usuario</th>
                      <th style={{ width: '12%' }}>Monto</th>
                      <th style={{ width: '10%' }}>Tipo</th>
                      <th style={{ width: '18%' }}>Fecha</th>
                      <th style={{ width: '10%', textAlign: 'right' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bids.map((b) => (
                      <tr key={b._id}>
                        <td>{b.displayName} (@{b.twitchUsername})</td>
                        <td>${b.amount}</td>
                        <td>{b.isManual ? 'Manual' : 'Twitch'}</td>
                        <td>{new Date(b.createdAt).toLocaleString('es-AR')}</td>
                        <td style={{ textAlign: 'right' }}>
                          <Button $variant="danger" $size="sm" type="button" onClick={() => removeBid(b._id)}>×</Button>
                        </td>
                      </tr>
                    ))}
                    {!bids.length && (
                      <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem' }}>Sin pujas</td></tr>
                    )}
                  </tbody>
                </Table>
              </TableWrap>
            </SurfaceBody>
          </Surface>
        </>
      )}

      {tab === 'acciones' && (
        <>
          <Surface>
            <SurfaceHeader>
              <SurfaceTitle>Finalizar subasta</SurfaceTitle>
            </SurfaceHeader>
            <SurfaceBody>
              <Muted>
                Podés cerrarla ahora o programar fecha/hora. Al cerrar se anuncia ganador en chat y alerta OBS.
              </Muted>
              {isClosed ? (
                <p style={{ color: '#b8a9c9', marginTop: '0.65rem' }}>
                  Cerrada{auction.closedAt ? ` el ${new Date(auction.closedAt).toLocaleString('es-AR')}` : ''}.
                </p>
              ) : (
                <>
                  <FormRow style={{ marginTop: '0.65rem' }}>
                    <Label>Cierre programado</Label>
                    <Input
                      type="datetime-local"
                      value={edit.endsAt}
                      onChange={(e) => setEdit({ ...edit, endsAt: e.target.value })}
                    />
                    <Muted>Guardá acá para programar el cierre. El backend cierra solo al llegar la hora.</Muted>
                  </FormRow>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.65rem' }}>
                    <Button type="button" onClick={saveScheduledClose}>
                      Guardar cierre programado
                    </Button>
                    <Button $variant="danger" type="button" onClick={finalizeNow}>
                      Finalizar ahora
                    </Button>
                  </div>
                </>
              )}
            </SurfaceBody>
          </Surface>

          <Surface>
            <SurfaceHeader>
              <SurfaceTitle>Bloquear usuario</SurfaceTitle>
            </SurfaceHeader>
            <SurfaceBody>
              <form onSubmit={blockUser}>
                <FormGrid>
                  <FormRow>
                    <Label>Twitch user ID</Label>
                    <Input value={block.twitchUserId} onChange={(e) => setBlock({ ...block, twitchUserId: e.target.value })} />
                  </FormRow>
                  <FormRow>
                    <Label>Username</Label>
                    <Input value={block.twitchUsername} onChange={(e) => setBlock({ ...block, twitchUsername: e.target.value })} />
                  </FormRow>
                  <FormRow style={{ gridColumn: '1 / -1' }}>
                    <Label>Motivo</Label>
                    <Input value={block.reason} onChange={(e) => setBlock({ ...block, reason: e.target.value })} />
                  </FormRow>
                </FormGrid>
                <Button $variant="danger" type="submit" style={{ marginTop: '0.65rem' }}>Bloquear</Button>
              </form>
            </SurfaceBody>
          </Surface>

          <Surface style={{ borderColor: 'rgba(255, 77, 109, 0.45)' }}>
            <SurfaceHeader>
              <SurfaceTitle style={{ color: '#ff6b8a' }}>Zona peligrosa</SurfaceTitle>
            </SurfaceHeader>
            <SurfaceBody>
              <Muted>
                Eliminar borra la subasta y todo su historial de pujas del sistema. No es lo mismo que finalizar.
              </Muted>
              <Button $variant="danger" type="button" style={{ marginTop: '0.65rem' }} onClick={deleteAuction}>
                Eliminar subasta
              </Button>
            </SurfaceBody>
          </Surface>
        </>
      )}
    </Page>
  );
};

export default AuctionDetailAdminPage;
